import React, { useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { checkAuthStatus, sessionExpired } from "./store";
import { useTokenRefresh } from "./hooks/useTokenRefresh";
import { onSessionExpired } from "./services/sessionEvents";
import LoadingSpinner from "./components/common/LoadingSpinner";
import ProtectedRoute from "./components/auth/ProtectedRoute";

// Lazy load components
const LoginForm = React.lazy(() => import("./components/auth/LoginForm"));
const TwoFactorForm = React.lazy(
  () => import("./components/auth/TwoFactorForm"),
);
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Organizations = React.lazy(() => import("./pages/Organizations"));
const OrganizationDetail = React.lazy(
  () => import("./pages/OrganizationDetail"),
);
const OrganizationUsers = React.lazy(
  () => import("./pages/OrganizationUsers"),
);
const OrganizationParticipants = React.lazy(
  () => import("./pages/OrganizationParticipants"),
);
const OrganizationTests = React.lazy(
  () => import("./pages/OrganizationTests"),
);
const AdminInterventions = React.lazy(
  () => import("./pages/AdminInterventions"),
);
const AdminData = React.lazy(() => import("./pages/AdminData"));
const Participants = React.lazy(() => import("./pages/Participants"));
const ParticipantProgress = React.lazy(
  () => import("./pages/ParticipantProgress"),
);
const Users = React.lazy(() => import("./pages/Users"));
const Tests = React.lazy(() => import("./pages/Tests"));
const TestAttendance = React.lazy(() => import("./pages/TestAttendance"));
const TestProgress = React.lazy(() => import("./pages/TestProgress"));
const TestResultsOverview = React.lazy(
  () => import("./pages/TestResultsOverview"),
);
const TestParticipantSelection = React.lazy(
  () => import("./pages/TestParticipantSelection"),
);
const TestParticipantOrderPreview = React.lazy(
  () => import("./pages/TestParticipantOrderPreview"),
);
const TestTimerScreen = React.lazy(() => import("./pages/TestTimerScreen"));
const TestMeasurementScreen = React.lazy(
  () => import("./pages/TestMeasurementScreen"),
);
const Settings = React.lazy(() => import("./pages/Settings"));
const BulkEntryPage = React.lazy(() => import("./pages/BulkEntryPage"));
const MentalHealthQrPage = React.lazy(
  () => import("./pages/MentalHealthQrPage"),
);
const PublicQuestionnaire = React.lazy(
  () => import("./pages/PublicQuestionnaire"),
);

const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, requiresTwoFactor } = useAppSelector(
    (state) => state.auth,
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (requiresTwoFactor) {
    return <TwoFactorForm />;
  }

  if (isAuthenticated) {
    // Get the intended destination from URL params or default to dashboard
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTo = urlParams.get("redirect") || "/dashboard";
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

const LandingRoute: React.FC = () => {
  const { isAuthenticated, isLoading, requiresTwoFactor } = useAppSelector(
    (state) => state.auth,
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (requiresTwoFactor) {
    return <TwoFactorForm />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/login" replace />;
};

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(checkAuthStatus());
  }, [dispatch]);

  // A failed refresh (services/api.ts) opens the re-login dialog.
  useEffect(
    () => onSessionExpired(() => dispatch(sessionExpired())),
    [dispatch],
  );

  // One refresh timer for the whole app (not per route).
  useTokenRefresh();

  return (
    <Router>
      <div className="App">
        <Suspense
          fallback={<LoadingSpinner size="large" className="min-h-screen" />}
        >
          <Routes>
            <Route
              path="/login"
              element={
                <AuthRoute>
                  <LoginForm />
                </AuthRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations"
              element={
                <ProtectedRoute>
                  <Organizations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations/:id"
              element={
                <ProtectedRoute>
                  <OrganizationDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations/:orgId/users"
              element={
                <ProtectedRoute>
                  <OrganizationUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations/:orgId/participants"
              element={
                <ProtectedRoute>
                  <OrganizationParticipants />
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations/:orgId/tests"
              element={
                <ProtectedRoute>
                  <OrganizationTests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/interventions"
              element={
                <ProtectedRoute>
                  <AdminInterventions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/data"
              element={
                <ProtectedRoute>
                  <AdminData />
                </ProtectedRoute>
              }
            />
            <Route
              path="/participants"
              element={
                <ProtectedRoute>
                  <Participants />
                </ProtectedRoute>
              }
            />
            <Route
              path="/participants/:id/progress"
              element={
                <ProtectedRoute>
                  <ParticipantProgress />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tests"
              element={
                <ProtectedRoute>
                  <Tests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tests/:id/attendance"
              element={
                <ProtectedRoute>
                  <TestAttendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tests/:id/progress"
              element={
                <ProtectedRoute>
                  <TestProgress />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tests/:id/bulk-entry"
              element={
                <ProtectedRoute>
                  <BulkEntryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tests/:id/progress/participants/:taskId"
              element={
                <ProtectedRoute>
                  <TestParticipantSelection />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tests/:id/progress/participants/:taskId/order"
              element={
                <ProtectedRoute>
                  <TestParticipantOrderPreview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tests/:id/progress/timer/:taskId"
              element={
                <ProtectedRoute>
                  <TestTimerScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tests/:id/progress/measurement/:taskId"
              element={
                <ProtectedRoute>
                  <TestMeasurementScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tests/:id/results"
              element={
                <ProtectedRoute>
                  <TestResultsOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tests/:id/mental-health/qr"
              element={
                <ProtectedRoute>
                  <MentalHealthQrPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            {/* Public, unauthenticated participant questionnaire (QR / one-time link) */}
            <Route path="/q/:token" element={<PublicQuestionnaire />} />
            <Route path="/" element={<LandingRoute />} />
          </Routes>
        </Suspense>
      </div>
    </Router>
  );
};

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
